import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AuthUser } from '../../types';
import { useTheme } from '../../theme/ThemeContext';
import { getTokens } from '../../theme/tokens';
import { NAVBAR_HEIGHT } from './Navbar';
import useNavItems from './useNavItems';
import { Icon, iconFor } from '../ui';

// The "Top menu bar" navigation layout: a row under the top bar with one
// menu per category; clicking (not hovering, so it works on touch screens)
// drops the category's modules down. Wraps onto a second row on a narrow
// screen rather than scrolling, so a dropdown is never clipped.

export interface TopMenuProps {
  user: AuthUser | null;
}

const TopMenu = ({ user }: TopMenuProps) => {
  const { isDarkMode } = useTheme();
  const t = getTokens(isDarkMode);
  const navigate = useNavigate();
  const nav = useNavItems(user);

  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const barRef = useRef<HTMLElement>(null);

  // Any click outside the bar, or Escape, closes the open dropdown.
  useEffect(() => {
    if (!openCategory) return undefined;
    const onDown = (e: MouseEvent) => {
      if (barRef.current && !barRef.current.contains(e.target as Node)) setOpenCategory(null);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenCategory(null); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openCategory]);

  const go = useCallback((path: string) => {
    setOpenCategory(null);
    navigate(path);
  }, [navigate]);

  const barItem = (active: boolean, open = false): CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: '6px',
    padding: '8px 14px', minHeight: '38px', cursor: 'pointer', whiteSpace: 'nowrap',
    border: 'none', borderRadius: t.radius.pill,
    backgroundColor: active || open ? t.primarySoft : 'transparent',
    color: active || open ? t.textAccent : t.textMain,
    fontWeight: active ? t.fontWeight.bold : t.fontWeight.medium,
    fontSize: t.fontSize.md, transition: 'background-color 0.15s ease'
  });

  return (
    <nav
      ref={barRef}
      aria-label="Main menu"
      style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px',
        padding: '6px 20px', position: 'sticky', top: NAVBAR_HEIGHT, zIndex: t.zIndex.navbar - 1,
        backgroundColor: t.bgMain, borderBottom: `1px solid ${t.border}`, boxShadow: t.shadowRaised
      }}
    >
      <button type="button" onClick={() => go('/')} aria-current={nav.atHome ? 'page' : undefined} style={barItem(nav.atHome)}>
        <span aria-hidden="true">🏠</span> Home
      </button>

      {nav.groups.map((group) => {
        const open = openCategory === group.category;
        const active = group.items.some((item) => item.id === nav.activeModuleId);
        return (
          <div key={group.category} style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setOpenCategory(open ? null : group.category)}
              aria-expanded={open}
              aria-haspopup="menu"
              style={barItem(active, open)}
            >
              {group.category}
              <span aria-hidden="true" style={{ fontSize: t.fontSize.xs, opacity: 0.7 }}>{open ? '▴' : '▾'}</span>
            </button>

            {open && (
              <div
                role="menu"
                style={{
                  position: 'absolute', top: 'calc(100% + 6px)', left: 0, minWidth: '240px',
                  padding: '6px', borderRadius: t.radius.lg, zIndex: t.zIndex.drawer,
                  backgroundColor: t.bgRaised, border: `1px solid ${t.border}`, boxShadow: t.shadowCard
                }}
              >
                {group.items.map((item) => {
                  const current = item.id === nav.activeModuleId;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="menuitem"
                      onClick={() => go(item.path)}
                      aria-current={current ? 'page' : undefined}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                        padding: '9px 12px', border: 'none', borderRadius: t.radius.md, cursor: 'pointer',
                        textAlign: 'left', whiteSpace: 'nowrap',
                        backgroundColor: current ? t.primarySoft : 'transparent',
                        color: current ? t.textAccent : t.textMain,
                        fontWeight: current ? t.fontWeight.bold : t.fontWeight.normal,
                        fontSize: t.fontSize.md
                      }}
                    >
                      <span style={{ display: 'inline-flex', width: '22px', justifyContent: 'center' }} aria-hidden="true"><Icon name={iconFor(item.icon)} size={18} /></span>
                      {item.title}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {nav.settings && (
        <button
          type="button"
          onClick={() => go(nav.settings!.path)}
          aria-current={nav.activeModuleId === nav.settings.id ? 'page' : undefined}
          style={{ ...barItem(nav.activeModuleId === nav.settings.id), marginLeft: 'auto' }}
        >
          <Icon name={iconFor(nav.settings.icon, 'settings')} size={16} /> {nav.settings.title}
        </button>
      )}
    </nav>
  );
};

export default TopMenu;
